/**
 * StatusEffect — framework-level buff/debuff that can be applied to any character.
 *
 * Effects are identified by string id, can stack, and provide flat stat modifiers.
 * The character queries its effects to resolve final stats at combat time.
 *
 * This is a simple data container — the game layer defines specific effects
 * (e.g. "alert", "poison") and interprets what the modifiers mean.
 */

export interface StatModifier {
  /** The stat key this modifies (e.g. "attack", "armor", "maxHp"). */
  stat: string;
  /** Flat bonus per stack. */
  value: number;
}

export interface StatusEffectDef {
  id: string;
  name: string;
  /** Per-stack stat modifiers. */
  modifiers: StatModifier[];
}

export class StatusEffect {
  readonly id: string;
  readonly name: string;
  readonly modifiers: StatModifier[];
  stacks: number;

  constructor(def: StatusEffectDef, stacks: number = 1) {
    this.id = def.id;
    this.name = def.name;
    this.modifiers = def.modifiers;
    this.stacks = stacks;
  }

  /** Get the total value of a specific stat modifier (value × stacks). */
  getModifier(stat: string): number {
    const mod = this.modifiers.find(m => m.stat === stat);
    return mod ? mod.value * this.stacks : 0;
  }
}

/**
 * StatusEffectBag — collection of active status effects on a character.
 */
export class StatusEffectBag {
  private effects: Map<string, StatusEffect> = new Map();

  /** Apply an effect. If already present, adds stacks. */
  apply(def: StatusEffectDef, stacks: number = 1): void {
    const existing = this.effects.get(def.id);
    if (existing) {
      existing.stacks += stacks;
    } else {
      this.effects.set(def.id, new StatusEffect(def, stacks));
    }
  }

  /** Remove an effect entirely. */
  remove(id: string): void {
    this.effects.delete(id);
  }

  /** Check if an effect is active. */
  has(id: string): boolean {
    return this.effects.has(id);
  }

  /** Get a specific effect (or null). */
  get(id: string): StatusEffect | null {
    return this.effects.get(id) ?? null;
  }

  /** Get total modifier for a stat across all active effects. */
  totalModifier(stat: string): number {
    let total = 0;
    for (const effect of this.effects.values()) {
      total += effect.getModifier(stat);
    }
    return total;
  }

  /** All active effects. */
  all(): StatusEffect[] {
    return Array.from(this.effects.values());
  }

  /** Remove all effects. */
  clear(): void {
    this.effects.clear();
  }
}
